import {
  availableLivestreamProviderNames,
  BLOCK_CATALOG,
  blockSchema,
  isLivestreamAddress,
  isLivestreamProviderAvailable,
  LIVESTREAM_PROVIDER_KEYS,
  LIVESTREAM_PROVIDER_LABELS,
  livestreamProviderOf,
  livestreamSummary,
  type BlockType,
  type LivestreamProvider,
} from "@how/website-core";

/**
 * How each block type is edited, as data rather than twelve hand-written forms. The contracts in
 * `@how/website-core` decide what is valid; this decides only which control edits which field and what staff see.
 */
export type Field =
  | {
      readonly kind: "text";
      readonly key: string;
      readonly label: string;
      readonly required?: boolean;
      readonly description?: string;
      /**
       * A check shown under the field as staff type, before they press Done: the contract's own rule, said where the
       * mistake is. Given the field's value and the block's other values; null when there is nothing to say.
       */
      readonly check?: (value: string, block: Readonly<Record<string, unknown>>) => string | null;
    }
  | {
      readonly kind: "textarea";
      readonly key: string;
      readonly label: string;
      readonly required?: boolean;
    }
  | {
      readonly kind: "select";
      readonly key: string;
      readonly label: string;
      readonly options: readonly {
        readonly value: string;
        readonly label: string;
        /** Shown in the list but not choosable, such as a provider that is coming soon. */
        readonly disabled?: boolean;
        /** A short badge beside the option, such as "Coming soon". */
        readonly badge?: string;
      }[];
    }
  | { readonly kind: "time"; readonly key: string; readonly label: string }
  /** A whole number between two bounds, such as a stream's length in minutes. */
  | {
      readonly kind: "number";
      readonly key: string;
      readonly label: string;
      readonly min: number;
      readonly max: number;
      readonly initial: number;
      /** After the number; the word is singular for 1 ("1 minute before", "2 minutes before"). */
      readonly suffix?: string;
      readonly suffixOne?: string;
      /** How far the arrows move it; 5 when absent. */
      readonly step?: number;
      readonly description?: string;
      /** Shown only while this holds for the block's other values (a countdown's lead, while the countdown is on). */
      readonly showIf?: (block: Readonly<Record<string, unknown>>) => boolean;
    }
  | {
      readonly kind: "checkbox";
      readonly key: string;
      readonly label: string;
      readonly description?: string;
      /** A new block's value; false when absent. */
      readonly initial?: boolean;
    }
  /**
   * The Live now block's stream address: checked against the chosen provider as staff type, an embed code reduced to
   * its address, the provider switched to the one the address belongs to, and a YouTube handle's channel found.
   */
  | { readonly kind: "stream"; readonly key: string; readonly label: string }
  /** Which campus's time zone a block's times are in; empty is the church's own. */
  | { readonly kind: "campus"; readonly key: string; readonly label: string }
  /**
   * Which sermons a sermon feature block draws (ADR 0061): the latest, a series's latest, or one sermon, chosen from
   * the church's own series and published sermons.
   */
  | { readonly kind: "sermonSource"; readonly key: string; readonly label: string }
  | {
      readonly kind: "link";
      readonly key: string;
      readonly label: string;
      readonly required?: boolean;
    }
  | { readonly kind: "image"; readonly key: string; readonly label: string }
  | { readonly kind: "richtext"; readonly key: string; readonly label: string }
  /** A list of short text lines, one per line, such as the lines of an address. */
  | { readonly kind: "lines"; readonly key: string; readonly label: string }
  /** One of the church's forms. */
  | { readonly kind: "form"; readonly key: string; readonly label: string }
  | {
      readonly kind: "list";
      readonly key: string;
      readonly label: string;
      readonly itemLabel: string;
      readonly fields: readonly Field[];
      readonly max: number;
    };

const variant = (...options: readonly [string, string][]): Field => ({
  kind: "select",
  key: "variant",
  label: "Layout",
  options: options.map(([value, label]) => ({ value, label })),
});

const DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"].map(
  (day) => ({
    value: day,
    label: `${day.charAt(0).toUpperCase()}${day.slice(1)}`,
  }),
);

const heading: Field = { kind: "text", key: "heading", label: "Heading", required: true };

/**
 * Every provider, in Planning Center's order; the ones not yet available (AVAILABLE_LIVESTREAM_PROVIDERS: Resi, BoxCast
 * and Facebook, until counsel answers) show with a "Coming soon" badge and cannot be chosen.
 */
const LIVESTREAM_PROVIDERS = LIVESTREAM_PROVIDER_KEYS.map((value) =>
  isLivestreamProviderAvailable(value)
    ? { value, label: LIVESTREAM_PROVIDER_LABELS[value] }
    : { value, label: LIVESTREAM_PROVIDER_LABELS[value], disabled: true, badge: "Coming soon" },
);

/** "Facebook streams are coming soon. For now, choose YouTube or Vimeo." */
export function comingSoonMessage(provider: LivestreamProvider): string {
  return `${LIVESTREAM_PROVIDER_LABELS[provider]} streams are coming soon. For now, choose ${availableLivestreamProviderNames()}.`;
}

/** What to paste, per provider: said under the address, and again when what was pasted is not it. */
export const STREAM_ADDRESS_HELP: Readonly<Record<LivestreamProvider, string>> = {
  youtube:
    "Your channel's live address works every week: https://www.youtube.com/@YourChurch/live or https://www.youtube.com/channel/UC…/live. A single video's address works too.",
  vimeo:
    "A recurring event works every week: https://vimeo.com/event/… A single video's address works too.",
  resi: "Your web channel's address from Resi Studio: https://control.resi.io/webplayer/video.html?id=… You can paste the whole embed code; only the address is kept.",
  boxcast:
    "Your channel's address works every week: https://boxcast.tv/channel/… A broadcast's address (boxcast.tv/view/…) works too. You can paste the embed code; only the address is kept.",
  facebook:
    "A video's address plays in the page: https://www.facebook.com/YourChurch/videos/… Your page's live address (https://www.facebook.com/YourChurch/live) shows a Watch live on Facebook button instead.",
};

function isProvider(value: unknown): value is LivestreamProvider {
  return typeof value === "string" && value in LIVESTREAM_PROVIDER_LABELS;
}

/** The Live now block's stream address, checked against the chosen provider as staff type it. */
export function streamAddressProblem(
  value: string,
  block: Readonly<Record<string, unknown>>,
): string | null {
  const provider = block["provider"];
  if (isProvider(provider) && !isLivestreamProviderAvailable(provider)) {
    return comingSoonMessage(provider);
  }
  if (value.trim() === "" || !isProvider(provider)) return null;
  // An address from a provider that is coming soon says so, rather than "not a YouTube address".
  const owner = livestreamProviderOf(value.trim());
  if (owner !== null && !isLivestreamProviderAvailable(owner)) return comingSoonMessage(owner);
  if (isLivestreamAddress(provider, value.trim())) return null;
  return `Not a ${LIVESTREAM_PROVIDER_LABELS[provider]} stream address. ${STREAM_ADDRESS_HELP[provider]}`;
}

function replayAddressProblem(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" || /^(\/|https:\/\/)/.test(trimmed)
    ? null
    : "Starts with / for a page on your site, or https://";
}

/** Every block type staff can add today. Types whose data or module does not exist yet are not here. */
export const BLOCK_FIELDS: Partial<Record<BlockType, readonly Field[]>> = {
  hero: [
    variant(
      ["standard", "Standard"],
      ["split", "Text beside the image"],
      ["image_background", "Text over the image"],
    ),
    {
      kind: "text",
      key: "headline",
      label: "Headline",
      required: true,
      description: "The page's main heading.",
    },
    { kind: "textarea", key: "subheadline", label: "Subheadline" },
    { kind: "link", key: "primaryAction", label: "Main button" },
    { kind: "link", key: "secondaryAction", label: "Second button" },
    { kind: "image", key: "image", label: "Image" },
  ],
  service_times: [
    variant(["list", "List"], ["cards", "Cards"]),
    heading,
    {
      kind: "list",
      key: "services",
      label: "Services",
      itemLabel: "Service",
      max: 12,
      fields: [
        { kind: "text", key: "name", label: "Name", required: true },
        { kind: "select", key: "day", label: "Day", options: DAYS },
        { kind: "time", key: "time", label: "Time" },
        { kind: "text", key: "note", label: "Note" },
      ],
    },
  ],
  locations: [
    variant(["list", "List"], ["map", "With map links"]),
    heading,
    {
      kind: "list",
      key: "locations",
      label: "Locations",
      itemLabel: "Location",
      max: 20,
      fields: [
        { kind: "text", key: "name", label: "Name", required: true },
        { kind: "lines", key: "addressLines", label: "Address, one line per line" },
        { kind: "text", key: "phone", label: "Phone" },
        {
          kind: "text",
          key: "mapUrl",
          label: "Map link",
          description: "An https address, such as a Google Maps link.",
        },
      ],
    },
  ],
  mission: [
    variant(["statement", "Statement"], ["values", "Statement and values"]),
    heading,
    { kind: "textarea", key: "statement", label: "Statement", required: true },
    {
      kind: "list",
      key: "values",
      label: "Values",
      itemLabel: "Value",
      max: 8,
      fields: [
        { kind: "text", key: "title", label: "Value", required: true },
        { kind: "textarea", key: "description", label: "What it means" },
      ],
    },
  ],
  ministry_grid: [
    variant(["grid", "Grid"], ["list", "List"]),
    heading,
    {
      kind: "list",
      key: "items",
      label: "Ministries",
      itemLabel: "Ministry",
      max: 24,
      fields: [
        { kind: "text", key: "title", label: "Name", required: true },
        { kind: "textarea", key: "description", label: "Description" },
        { kind: "image", key: "image", label: "Image" },
        { kind: "link", key: "link", label: "Link" },
      ],
    },
  ],
  staff_grid: [
    variant(["grid", "Grid"], ["list", "List"]),
    heading,
    {
      kind: "list",
      key: "people",
      label: "People",
      itemLabel: "Person",
      max: 48,
      fields: [
        { kind: "text", key: "name", label: "Name", required: true },
        { kind: "text", key: "role", label: "Role", required: true },
        { kind: "textarea", key: "bio", label: "Short bio" },
        { kind: "image", key: "image", label: "Photo" },
        { kind: "text", key: "email", label: "Email" },
      ],
    },
  ],
  giving: [
    variant(["simple", "Simple"], ["split", "Beside an image"]),
    heading,
    { kind: "textarea", key: "message", label: "Message" },
    { kind: "link", key: "action", label: "Button", required: true },
    { kind: "image", key: "image", label: "Image" },
  ],
  plan_visit: [
    variant(["standard", "Standard"], ["steps", "Numbered steps"]),
    heading,
    { kind: "textarea", key: "intro", label: "Introduction" },
    {
      kind: "list",
      key: "steps",
      label: "Steps",
      itemLabel: "Step",
      max: 6,
      fields: [
        { kind: "text", key: "title", label: "Step", required: true },
        { kind: "textarea", key: "description", label: "Details" },
      ],
    },
    { kind: "link", key: "action", label: "Button" },
  ],
  contact_form: [
    variant(["standard", "Standard"], ["split", "Beside the text"]),
    heading,
    { kind: "textarea", key: "description", label: "Introduction" },
    { kind: "form", key: "formId", label: "Form" },
  ],
  newsletter: [
    variant(["inline", "In the page"], ["card", "In a card"]),
    heading,
    { kind: "textarea", key: "description", label: "Introduction" },
    { kind: "form", key: "formId", label: "Form" },
  ],
  rich_text: [
    variant(["standard", "Standard"], ["narrow", "Narrow column"]),
    { kind: "richtext", key: "body", label: "Text" },
  ],
  faq: [
    variant(["accordion", "Expandable"], ["list", "All open"]),
    heading,
    {
      kind: "list",
      key: "items",
      label: "Questions",
      itemLabel: "Question",
      max: 30,
      fields: [
        { kind: "text", key: "question", label: "Question", required: true },
        { kind: "richtext", key: "answer", label: "Answer" },
      ],
    },
  ],
  media_gallery: [
    variant(["grid", "Grid"], ["carousel", "Carousel"]),
    { kind: "text", key: "heading", label: "Heading" },
    {
      kind: "list",
      key: "images",
      label: "Images",
      itemLabel: "Image",
      max: 24,
      fields: [
        { kind: "image", key: "image", label: "Image" },
        { kind: "text", key: "caption", label: "Caption" },
      ],
    },
  ],
  custom_embed: [
    variant(["wide", "Wide"], ["standard", "Standard"], ["square", "Square"]),
    {
      kind: "select",
      key: "provider",
      label: "From",
      options: [
        { value: "youtube", label: "YouTube" },
        { value: "vimeo", label: "Vimeo" },
        { value: "google_maps", label: "Google Maps" },
        { value: "spotify", label: "Spotify" },
      ],
    },
    {
      kind: "text",
      key: "url",
      label: "Address",
      required: true,
      description:
        "Paste the page address from the provider, such as https://www.youtube.com/watch?v=… For Google Maps, use the address from Share, then Embed a map.",
    },
    {
      kind: "text",
      key: "title",
      label: "What it is",
      required: true,
      description: "Read aloud by screen readers.",
    },
    { kind: "text", key: "caption", label: "Caption" },
  ],
  sermon_feature: [
    variant(
      ["latest", "The newest sermon"],
      ["featured", "The newest sermon, with its video"],
      ["list", "A list of sermons"],
    ),
    heading,
    { kind: "sermonSource", key: "source", label: "Which sermons" },
    {
      kind: "number",
      key: "limit",
      label: "How many",
      min: 1,
      max: 12,
      initial: 3,
      step: 1,
      suffix: "sermons",
      suffixOne: "sermon",
      showIf: (block) => block["variant"] === "list",
    },
    { kind: "checkbox", key: "showSpeaker", label: "Show the speaker", initial: true },
  ],
  livestream: [
    variant(["player", "Player in the page"], ["button", "Watch live button"]),
    heading,
    {
      kind: "select",
      key: "provider",
      label: "Stream from",
      options: LIVESTREAM_PROVIDERS,
    },
    { kind: "stream", key: "url", label: "Stream address" },
    { kind: "campus", key: "campusId", label: "Times are in" },
    {
      kind: "list",
      key: "services",
      label: "When you are live",
      itemLabel: "Service",
      max: 14,
      fields: [
        { kind: "select", key: "day", label: "Day", options: DAYS },
        { kind: "time", key: "start", label: "Starts" },
        {
          kind: "number",
          key: "durationMinutes",
          label: "Live for",
          min: 5,
          max: 360,
          initial: 90,
          suffix: "minutes",
          suffixOne: "minute",
        },
      ],
    },
    {
      kind: "number",
      key: "openEarlyMinutes",
      label: "Open the player",
      min: 0,
      max: 60,
      initial: 0,
      step: 5,
      suffix: "minutes early",
      suffixOne: "minute early",
      description:
        "Visitors can press play this long before each service starts, for a pre-service countdown or music. 0 opens it at the start time.",
    },
    {
      kind: "checkbox",
      key: "countdown",
      label: "Count down before each service",
      description:
        "Shows “Starts in 4:32” (“Opens in 4:32” when the player opens early) and opens the player on time. Turn it off if your stream shows its own countdown.",
      initial: true,
    },
    {
      kind: "number",
      key: "countdownMinutes",
      label: "Start the countdown",
      min: 1,
      max: 10_080,
      initial: 60,
      step: 15,
      suffix: "minutes before",
      suffixOne: "minute before",
      showIf: (block) => block["countdown"] !== false,
    },
    {
      kind: "number",
      key: "replayHours",
      label: "Show the replay link for",
      min: 0,
      max: 168,
      initial: 24,
      step: 1,
      suffix: "hours",
      suffixOne: "hour",
      description: "After each service. 0 turns the replay link off; 168 is a week.",
    },
    {
      kind: "text",
      key: "replayUrl",
      label: "Replay link",
      description: "Leave empty to link to the stream's own address.",
      check: replayAddressProblem,
    },
    {
      kind: "textarea",
      key: "upcomingMessage",
      label: "Message while you are not live",
    },
  ],
};

export const EDITABLE_TYPES = BLOCK_CATALOG.filter(
  (entry) => BLOCK_FIELDS[entry.type] !== undefined,
);

/** A new block of a type, with its first layout and one empty item in each list, ready to fill in. */
export function newBlock(type: BlockType): Record<string, unknown> {
  const block: Record<string, unknown> = { type };
  for (const field of BLOCK_FIELDS[type] ?? []) {
    if (field.kind === "select") block[field.key] = field.options[0]?.value;
    if (field.kind === "list") block[field.key] = [emptyItem(field.fields)];
    if (field.kind === "checkbox") block[field.key] = field.initial ?? false;
    if (field.kind === "number") block[field.key] = field.initial;
    if (field.kind === "sermonSource") block[field.key] = { mode: "latest" };
  }
  if (type === "rich_text") block["body"] = [];
  return block;
}

export function emptyItem(fields: readonly Field[]): Record<string, unknown> {
  const item: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.kind === "select") item[field.key] = field.options[0]?.value;
    if (field.kind === "time") item[field.key] = "10:00";
    if (field.kind === "number") item[field.key] = field.initial;
    if (field.kind === "lines") item[field.key] = [];
    if (field.kind === "richtext") item[field.key] = [];
  }
  return item;
}

const SERVICE_START = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The Live now block's schedule in a line, from the services that are filled in well enough to say. */
function livestreamCardLine(block: Record<string, unknown>): string {
  const services = (Array.isArray(block["services"]) ? block["services"] : []).filter(
    (s): s is { day: "sunday"; start: string; durationMinutes: number } =>
      typeof s === "object" &&
      s !== null &&
      typeof (s as Record<string, unknown>)["day"] === "string" &&
      SERVICE_START.test(String((s as Record<string, unknown>)["start"])) &&
      Number.isInteger((s as Record<string, unknown>)["durationMinutes"]),
  );
  return livestreamSummary(services);
}

/** A short line naming a block in the page's block list. */
export function blockSummary(block: Record<string, unknown>): string {
  if (block["type"] === "livestream") {
    // "Watch online · Sundays 9:00 and 11:00 AM, 90 min": which stream, and when it goes live.
    const heading = typeof block["heading"] === "string" ? block["heading"].trim() : "";
    const when = livestreamCardLine(block);
    return [heading, when].filter((part) => part.length > 0).join(" · ");
  }
  for (const key of ["headline", "heading", "title"]) {
    const value = block[key];
    if (typeof value === "string" && value.trim().length > 0) return value;
  }
  return "";
}

/** The label staff saw for a field path such as services.0.time, for error messages. */
export function describePath(type: BlockType, path: readonly PropertyKey[]): string {
  const parts: string[] = [];
  let fields: readonly Field[] = BLOCK_FIELDS[type] ?? [];
  for (let i = 0; i < path.length; i += 1) {
    const key = path[i];
    const field = fields.find((f) => f.key === key);
    if (field === undefined) break;
    parts.push(field.label);
    if (field.kind === "list") {
      const index = path[i + 1];
      if (typeof index === "number") {
        parts.push(`${field.itemLabel} ${String(index + 1)}`);
        i += 1;
      }
      fields = field.fields;
    }
  }
  return parts.join(" › ");
}

/** Checks a block against its contract and says what is wrong in the words the form uses. */
export function validateBlock(
  block: Record<string, unknown>,
):
  | { readonly ok: true; readonly value: Record<string, unknown> }
  | { readonly ok: false; readonly problems: string[] } {
  const parsed = blockSchema.safeParse(block);
  if (parsed.success) return { ok: true, value: parsed.data };
  const type = block["type"] as BlockType;
  return {
    ok: false,
    problems: parsed.error.issues.map((issue) => {
      const where = describePath(type, issue.path);
      const what = plainMessage(issue);
      return where.length > 0 ? `${where}: ${what}` : what;
    }),
  };
}

interface Issue {
  readonly code: string;
  readonly message: string;
  readonly origin?: string;
  readonly minimum?: number | bigint;
  readonly maximum?: number | bigint;
  readonly input?: unknown;
}

/** Zod's messages are written for developers; these are the ones staff meet, said plainly. */
export function plainMessage(issue: Issue): string {
  if (issue.code === "invalid_type" && issue.input === undefined) return "is required";
  if (issue.code === "too_small" && issue.origin === "string") return "is required";
  if (issue.code === "too_small" && issue.origin === "array" && Number(issue.minimum) === 1) {
    return "is required";
  }
  if (issue.code === "too_small" && issue.origin === "array") {
    return `needs at least ${String(issue.minimum)}`;
  }
  if (issue.code === "too_big" && issue.origin === "string") {
    return `is too long (at most ${String(issue.maximum)} characters)`;
  }
  if (issue.code === "too_big" && issue.origin === "array")
    return `has more than ${String(issue.maximum)}`;
  // Messages the contracts wrote themselves (links, images, embeds) are already plain.
  if (issue.code === "custom" || !issue.message.startsWith("Invalid")) return issue.message;
  return "is not in the right form";
}
